import { Component, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { CompanyView } from '@api-proxies';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { CompanyService } from '@http/clients/company.service';
import { Company } from '@models/Company';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    EditorItemComponent,
    IconSanitizerService,
    IEditorItemConfig,
    ModalComponentBase,
    SnackbarNotificationService,
    ValidatorType
} from 'ngx-lib';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';

@Component({
    selector: 'app-modal-add-company',
    templateUrl: './modal-add-company.component.html',
    styleUrls: ['./modal-add-company.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, EditorItemComponent, LoadingButtonComponent]
})
export class ModalAddCompanyComponent extends ModalComponentBase<undefined, CompanyView> implements OnInit {
    factory: AppEditorFactory;

    configItems: IEditorItemConfig[];

    company: Company;

    isReadOnly: boolean;

    @ViewChildren('editorItem')
    editors?: QueryList<EditorItemComponent>;

    isLoading: boolean;

    closeIcon: SafeHtml;

    constructor(
        private readonly companyService: CompanyService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly awaiter: AwaiterService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        super();
        this.factory = new AppEditorFactory();
        this.company = new Company();
        this.isReadOnly = false;
        this.configItems = [];
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
        this.isLoading = false;
    }

    ngOnInit(): void {
        this.configItems = [
            {
                label: 'Name',
                componentId: EditorType.textInput,
                fieldName: 'name',
                bindContext: this.company,
                isReadOnly: this.isReadOnly,
                validations: [
                    { type: ValidatorType.required },
                    { type: ValidatorType.string, args: { maxLength: 100 } }
                ],
                additionalInfo: {
                    shouldTrimSpaces: true
                }
            },
            {
                label: 'Abbreviation',
                componentId: EditorType.textInput,
                fieldName: 'abbreviation',
                bindContext: this.company,
                isReadOnly: this.isReadOnly,
                validations: [{ type: ValidatorType.string, args: { maxLength: 50 } }]
            },
            {
                label: 'Website',
                componentId: EditorType.link,
                fieldName: 'webSite',
                bindContext: this.company,
                isReadOnly: this.isReadOnly,
                validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
            },
            {
                label: 'Linkedin',
                componentId: EditorType.link,
                fieldName: 'linkedin',
                bindContext: this.company,
                isReadOnly: this.isReadOnly,
                validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
            },
            {
                label: 'Type',
                componentId: EditorType.companyType,
                fieldName: 'companyTypeId',
                bindContext: this.company,
                isReadOnly: this.isReadOnly
            },
            {
                label: 'Markets',
                componentId: EditorType.entity,
                fieldName: 'companyMarkets',
                bindContext: this.company,
                entityId: 'companyMarket',
                isReadOnly: this.isReadOnly
            }
        ];
    }

    validate(): boolean {
        return this.editors?.map((editorItem) => editorItem.validate()).every((editorItem) => editorItem) ?? false;
    }

    async saveCompany(): Promise<void> {
        if (!this.validate()) return;

        await this.awaiter.awaitAction(
            'Saving Company...',
            async () => {
                const response = await this.companyService.save(this.company);
                if (response) this.snackbarNotificationService.success('Company created successfully.');
                this.close(response);
            },
            (loading) => (this.isLoading = loading)
        );
    }
}
